// src/pages/sessions/components/LiveSession/whiteboard/WhiteboardTextToolbar.jsx
import React, { useState, useRef, useEffect, memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

// Font options
const FONTS = [
  { label: 'Arial', value: 'Arial, sans-serif' },
  { label: 'Quicksand', value: 'Quicksand, sans-serif' },
  { label: 'Georgia', value: 'Georgia, serif' },
  { label: 'Courier', value: 'Courier New, monospace' },
  { label: 'Comic Sans', value: 'Comic Sans MS, cursive' },
  { label: 'Verdana', value: 'Verdana, sans-serif' },
];

const FONT_SIZES = [14, 16, 18, 20, 24, 28, 32, 36, 48, 64];

// Text colors — organized by luminance for quick scanning
const TEXT_COLOR_GROUPS = [
  {
    label: 'Neutrals',
    colors: ['#000000', '#374151', '#6B7280', '#9CA3AF', '#FFFFFF'],
  },
  {
    label: 'Colors',
    colors: ['#DC2626', '#EA580C', '#D97706', '#16A34A', '#2563EB', '#7C3AED', '#DB2777'],
  },
];

// Background colors — curated pastel set that looks good as note backgrounds
const BG_COLOR_GROUPS = [
  {
    label: 'Warm',
    colors: ['#fff9c4', '#fff3cd', '#ffe0b2', '#ffcdd2', '#f8bbd0'],
  },
  {
    label: 'Cool',
    colors: ['#e1bee7', '#c5cae9', '#bbdefb', '#b3e5fc', '#b2dfdb'],
  },
  {
    label: 'Natural',
    colors: ['#c8e6c9', '#dcedc8', '#f0f4c3', '#d7ccc8', '#f8f9fa'],
  },
  {
    label: 'Solid',
    colors: ['#ffffff', '#333333', 'transparent'],
  },
];

// Checkmark SVG for active state
const CheckMark = ({ light }) => (
  <svg
    className="w-3 h-3 absolute"
    viewBox="0 0 12 12"
    fill="none"
    stroke={light ? '#000' : '#fff'}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ filter: light ? 'none' : 'drop-shadow(0 1px 1px rgba(0,0,0,0.5))' }}
  >
    <path d="M2.5 6l2.5 2.5 4.5-5" />
  </svg>
);

// Determine if a hex color is light (for contrast check on checkmark)
const isLightColor = (hex) => {
  if (!hex || hex === 'transparent') return true;
  const c = hex.replace('#', '');
  if (c.length < 6) return true;
  const r = parseInt(c.substr(0, 2), 16);
  const g = parseInt(c.substr(2, 2), 16);
  const b = parseInt(c.substr(4, 2), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 > 160;
};

/**
 * WhiteboardTextToolbar - Floating toolbar for editing text widgets
 */
const WhiteboardTextToolbar = memo(({
  visible,
  position,
  textObject,
  fabricCanvas,
  onUpdate,
  onClose,
}) => {
  const { t } = useTranslation();
  const toolbarRef = useRef(null);
  const [showFontPicker, setShowFontPicker] = useState(false);
  const [showSizePicker, setShowSizePicker] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showBgColorPicker, setShowBgColorPicker] = useState(false);

  // Get current text properties
  const fontFamily = textObject?.fontFamily || 'Arial, sans-serif';
  const fontSize = textObject?.fontSize || 24;
  const fontWeight = textObject?.fontWeight || 'normal';
  const fontStyle = textObject?.fontStyle || 'normal';
  const textAlign = textObject?.textAlign || 'left';
  const fill = textObject?.fill || '#000000';
  const underline = textObject?.underline || false;
  const bgColor = textObject?.backgroundColor || '';

  const fontLabel = FONTS.find(f => f.value === fontFamily)?.label || 'Arial';

  const closeAllDropdowns = useCallback(() => {
    setShowFontPicker(false);
    setShowSizePicker(false);
    setShowColorPicker(false);
    setShowBgColorPicker(false);
  }, []);

  // Close dropdowns on outside click
  useEffect(() => {
    if (!visible) return;
    const handleClick = (e) => {
      if (toolbarRef.current && !toolbarRef.current.contains(e.target)) {
        closeAllDropdowns();
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [visible, closeAllDropdowns]);

  const updateProp = useCallback((props) => {
    if (!textObject || !fabricCanvas) return;
    textObject.set(props);
    fabricCanvas.requestRenderAll();
    onUpdate?.(props);
  }, [textObject, fabricCanvas, onUpdate]);

  if (!visible || !position || !textObject) return null;

  const ToolbarButton = ({ active, onClick, children, title }) => (
    <button
      onMouseDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick?.();
      }}
      className={`p-1.5 rounded-md transition-all ${
        active
          ? 'bg-indigo-500/20 text-indigo-300'
          : 'text-gray-300 hover:bg-white/10 hover:text-white'
      }`}
      title={title}
    >
      {children}
    </button>
  );

  const Divider = () => <div className="w-px h-5 bg-gray-600 mx-0.5" />;

  // ===== Color swatch component =====
  const ColorSwatch = ({ color, isActive, onClick, size = 28, title }) => {
    const isTransparent = color === 'transparent';
    const light = isLightColor(color);

    return (
      <button
        onMouseDown={(e) => {
          e.preventDefault();
          onClick(color);
        }}
        className="relative flex items-center justify-center rounded-lg group"
        style={{
          width: size,
          height: size,
          backgroundColor: isTransparent ? 'transparent' : color,
          backgroundImage: isTransparent
            ? 'linear-gradient(45deg, #555 25%, transparent 25%, transparent 75%, #555 75%), linear-gradient(45deg, #555 25%, transparent 25%, transparent 75%, #555 75%)'
            : 'none',
          backgroundSize: '8px 8px',
          backgroundPosition: '0 0, 4px 4px',
          border: isActive
            ? '2px solid rgba(99, 102, 241, 0.9)'
            : '1px solid ' + (isTransparent || light ? 'rgba(120,120,120,0.4)' : 'rgba(255,255,255,0.08)'),
          boxShadow: isActive
            ? '0 0 0 2px rgba(99, 102, 241, 0.3)'
            : 'none',
          transition: 'transform 120ms ease, box-shadow 120ms ease, border-color 120ms ease',
        }}
        title={title || color}
      >
        {isActive && <CheckMark light={light && !isTransparent} />}
      </button>
    );
  };

  // ===== Dropdown panel wrapper =====
  const PickerPanel = ({ children, align = 'right' }) => (
    <div
      className={'absolute top-full mt-2 z-20 ' + (align === 'right' ? 'right-0' : 'left-0')}
    >
      <div
        className="rounded-xl overflow-hidden"
        style={{
          background: 'linear-gradient(145deg, rgba(30,32,40,0.98), rgba(24,26,32,0.98))',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.5), 0 2px 8px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.04)',
        }}
      >
        {children}
      </div>
    </div>
  );

  // ===== Group label =====
  const GroupLabel = ({ children }) => (
    <div
      className="text-[10px] font-medium uppercase tracking-widest mb-1.5 px-0.5"
      style={{ color: 'rgba(160,165,180,0.7)' }}
    >
      {children}
    </div>
  );

  return (
    <div
      ref={toolbarRef}
      className="fixed z-[9998]"
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        transform: 'translate(-50%, -100%) translateY(-12px)',
      }}
    >
      <div className="bg-gray-800/95 backdrop-blur-sm rounded-xl shadow-2xl border border-gray-600/50 px-2 py-1.5 flex items-center gap-0.5">

        {/* Move handle */}
        <div className="text-gray-500 cursor-move px-1" title="Move">
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M10 9h4V6h3l-5-5-5 5h3v3zm-1 1H6V7l-5 5 5 5v-3h3v-4zm14 2l-5-5v3h-3v4h3v3l5-5zm-9 3h-4v3H7l5 5 5-5h-3v-3z" />
          </svg>
        </div>

        <Divider />

        {/* Font family picker */}
        <div className="relative">
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              closeAllDropdowns();
              setShowFontPicker(!showFontPicker);
            }}
            className="text-gray-200 text-xs px-2 py-1 rounded-md hover:bg-white/10 transition-colors min-w-[80px] text-left truncate"
            style={{ fontFamily }}
          >
            {fontLabel}
          </button>
          {showFontPicker && (
            <PickerPanel align="left">
              <div className="py-1 min-w-[160px] max-h-[200px] overflow-y-auto">
                {FONTS.map(font => (
                  <button
                    key={font.value}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      updateProp({ fontFamily: font.value });
                      setShowFontPicker(false);
                    }}
                    className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                      fontFamily === font.value ? 'bg-indigo-500/15 text-indigo-300' : 'text-gray-200 hover:bg-white/10'
                    }`}
                    style={{ fontFamily: font.value }}
                  >
                    {font.label}
                  </button>
                ))}
              </div>
            </PickerPanel>
          )}
        </div>

        {/* Font size picker */}
        <div className="relative">
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              closeAllDropdowns();
              setShowSizePicker(!showSizePicker);
            }}
            className="text-gray-200 text-xs px-2 py-1 rounded-md hover:bg-white/10 transition-colors min-w-[32px] text-center"
          >
            {fontSize}
          </button>
          {showSizePicker && (
            <PickerPanel align="left">
              <div className="py-1 min-w-[60px] max-h-[200px] overflow-y-auto">
                {FONT_SIZES.map(size => (
                  <button
                    key={size}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      updateProp({ fontSize: size });
                      setShowSizePicker(false);
                    }}
                    className={`w-full text-left px-3 py-1.5 text-sm transition-colors ${
                      fontSize === size ? 'bg-indigo-500/15 text-indigo-300' : 'text-gray-200 hover:bg-white/10'
                    }`}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </PickerPanel>
          )}
        </div>

        <Divider />

        {/* Bold */}
        <ToolbarButton
          active={fontWeight === 'bold'}
          onClick={() => updateProp({ fontWeight: fontWeight === 'bold' ? 'normal' : 'bold' })}
          title="Bold"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M15.6 10.79c.97-.67 1.65-1.77 1.65-2.79 0-2.26-1.75-4-4-4H7v14h7.04c2.09 0 3.71-1.7 3.71-3.79 0-1.52-.86-2.82-2.15-3.42zM10 6.5h3c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5h-3v-3zm3.5 9H10v-3h3.5c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5z" />
          </svg>
        </ToolbarButton>

        {/* Italic */}
        <ToolbarButton
          active={fontStyle === 'italic'}
          onClick={() => updateProp({ fontStyle: fontStyle === 'italic' ? 'normal' : 'italic' })}
          title="Italic"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M10 4v3h2.21l-3.42 8H6v3h8v-3h-2.21l3.42-8H18V4z" />
          </svg>
        </ToolbarButton>

        {/* Underline */}
        <ToolbarButton
          active={underline}
          onClick={() => updateProp({ underline: !underline })}
          title="Underline"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 17c3.31 0 6-2.69 6-6V3h-2.5v8c0 1.93-1.57 3.5-3.5 3.5S8.5 12.93 8.5 11V3H6v8c0 3.31 2.69 6 6 6zm-7 2v2h14v-2H5z" />
          </svg>
        </ToolbarButton>

        <Divider />

        {/* Text align */}
        <ToolbarButton
          active={textAlign === 'left'}
          onClick={() => updateProp({ textAlign: 'left' })}
          title="Align left"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M15 15H3v2h12v-2zm0-8H3v2h12V7zM3 13h18v-2H3v2zm0 8h18v-2H3v2zM3 3v2h18V3H3z" />
          </svg>
        </ToolbarButton>
        <ToolbarButton
          active={textAlign === 'center'}
          onClick={() => updateProp({ textAlign: 'center' })}
          title="Align center"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M7 15v2h10v-2H7zm-4 6h18v-2H3v2zm0-8h18v-2H3v2zm4-6v2h10V7H7zM3 3v2h18V3H3z" />
          </svg>
        </ToolbarButton>

        <Divider />

        {/* ===== TEXT COLOR PICKER ===== */}
        <div className="relative">
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              closeAllDropdowns();
              setShowColorPicker(!showColorPicker);
            }}
            className="p-1 rounded-md hover:bg-white/10 transition-colors flex flex-col items-center"
            title="Text color"
          >
            <svg className="w-4 h-4 text-gray-200" viewBox="0 0 24 24" fill="currentColor">
              <path d="M11 2L5.5 16h2.25l1.12-3h6.25l1.12 3h2.25L13 2h-2zm-1.38 9L12 4.67 14.38 11H9.62z" />
            </svg>
            <div
              className="rounded-full mt-px"
              style={{
                width: 14,
                height: 3,
                backgroundColor: fill,
                border: fill === '#FFFFFF' ? '1px solid rgba(120,120,120,0.5)' : 'none',
              }}
            />
          </button>

          {showColorPicker && (
            <PickerPanel>
              <div className="p-3 min-w-[200px]">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[11px] font-semibold text-gray-300 tracking-wide">Text Color</span>
                  <div className="flex items-center gap-1.5">
                    <div
                      className="w-4 h-4 rounded"
                      style={{
                        backgroundColor: fill,
                        border: isLightColor(fill) ? '1px solid rgba(120,120,120,0.4)' : '1px solid rgba(255,255,255,0.1)',
                      }}
                    />
                    <span className="text-[10px] text-gray-500 font-mono">{fill}</span>
                  </div>
                </div>

                {TEXT_COLOR_GROUPS.map((group) => (
                  <div key={group.label} className="mb-2.5 last:mb-0">
                    <GroupLabel>{group.label}</GroupLabel>
                    <div className="flex gap-1.5">
                      {group.colors.map(color => (
                        <ColorSwatch
                          key={color}
                          color={color}
                          isActive={fill.toLowerCase() === color.toLowerCase()}
                          onClick={(c) => {
                            updateProp({ fill: c });
                            setShowColorPicker(false);
                          }}
                          size={28}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </PickerPanel>
          )}
        </div>

        {/* ===== BACKGROUND COLOR PICKER ===== */}
        <div className="relative">
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              closeAllDropdowns();
              setShowBgColorPicker(!showBgColorPicker);
            }}
            className="p-1 rounded-md hover:bg-white/10 transition-colors flex flex-col items-center"
            title="Note color"
          >
            <svg className="w-4 h-4 text-gray-200" viewBox="0 0 24 24" fill="currentColor">
              <path d="M16.56 8.94L7.62 0 6.21 1.41l2.38 2.38-5.15 5.15a1.49 1.49 0 000 2.12l5.5 5.5c.29.29.68.44 1.06.44s.77-.15 1.06-.44l5.5-5.5c.59-.58.59-1.53 0-2.12zM5.21 10L10 5.21 14.79 10H5.21zM19 11.5s-2 2.17-2 3.5c0 1.1.9 2 2 2s2-.9 2-2c0-1.33-2-3.5-2-3.5z" />
            </svg>
            <div
              className="rounded-full mt-px"
              style={{
                width: 14,
                height: 3,
                backgroundColor: bgColor || 'transparent',
                backgroundImage: !bgColor
                  ? 'linear-gradient(45deg, #777 25%, transparent 25%, transparent 75%, #777 75%), linear-gradient(45deg, #777 25%, transparent 25%, transparent 75%, #777 75%)'
                  : 'none',
                backgroundSize: '4px 4px',
                backgroundPosition: '0 0, 2px 2px',
                border: !bgColor ? 'none' : isLightColor(bgColor) ? '1px solid rgba(120,120,120,0.4)' : 'none',
              }}
            />
          </button>

          {showBgColorPicker && (
            <PickerPanel>
              <div className="p-3 min-w-[200px]">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[11px] font-semibold text-gray-300 tracking-wide">Note Color</span>
                  <div
                    className="px-2 py-0.5 rounded text-[9px] font-medium"
                    style={{
                      backgroundColor: bgColor || 'transparent',
                      color: bgColor && !isLightColor(bgColor) ? '#fff' : '#333',
                      border: !bgColor || isLightColor(bgColor) ? '1px solid rgba(120,120,120,0.3)' : 'none',
                    }}
                  >
                    Aa
                  </div>
                </div>

                {BG_COLOR_GROUPS.map((group) => (
                  <div key={group.label} className="mb-2.5 last:mb-0">
                    <GroupLabel>{group.label}</GroupLabel>
                    <div className="flex gap-1.5 flex-wrap">
                      {group.colors.map(color => {
                        const isTransparent = color === 'transparent';
                        const isActive = isTransparent ? !bgColor : bgColor === color;
                        return (
                          <ColorSwatch
                            key={color}
                            color={color}
                            isActive={isActive}
                            onClick={() => {
                              updateProp({ backgroundColor: isTransparent ? '' : color });
                              setShowBgColorPicker(false);
                            }}
                            size={28}
                            title={isTransparent ? 'No background' : color}
                          />
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </PickerPanel>
          )}
        </div>

        <Divider />

        {/* Delete */}
        <ToolbarButton
          onClick={() => {
            if (fabricCanvas && textObject) {
              fabricCanvas.remove(textObject);
              fabricCanvas.discardActiveObject();
              fabricCanvas.requestRenderAll();
            }
            onClose?.();
          }}
          title="Remove"
        >
          <svg className="w-4 h-4 text-red-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </ToolbarButton>
      </div>
    </div>
  );
});

WhiteboardTextToolbar.displayName = 'WhiteboardTextToolbar';

export default WhiteboardTextToolbar;
